/**
 * POASection - TASK-376
 * Power of Attorney fields for Client Profile Section 4.
 * Fields: holder name, relationship, scope (limited/general), date signed.
 */

import React from 'react';
import { Scale } from 'lucide-react';
import { InheritedBadge } from './InheritedBadge';

export interface POAData {
  holder_name: string;
  relationship: string;
  scope: 'limited' | 'general' | '';
  date_signed: string;
  source?: string;
}

interface POASectionProps {
  data: POAData;
  onChange: (field: keyof POAData, value: string) => void;
  readOnly?: boolean;
}

export const POASection: React.FC<POASectionProps> = ({
  data,
  onChange,
  readOnly = false,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Scale className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-900">
          Power of Attorney
        </h4>
        {data.source === 'inherited' && <InheritedBadge />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            POA Holder Name
          </label>
          <input
            type="text"
            value={data.holder_name || ''}
            onChange={(e) => onChange('holder_name', e.target.value)}
            readOnly={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent read-only:bg-gray-50"
            placeholder="Full name of POA holder"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Relationship to Account Holder
          </label>
          <input
            type="text"
            value={data.relationship || ''}
            onChange={(e) => onChange('relationship', e.target.value)}
            readOnly={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent read-only:bg-gray-50"
            placeholder="e.g. Spouse, Attorney, Family member"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Scope
          </label>
          <select
            value={data.scope || ''}
            onChange={(e) => onChange('scope', e.target.value)}
            disabled={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
          >
            <option value="">Select scope</option>
            <option value="limited">Limited</option>
            <option value="general">General</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Date Signed
          </label>
          <input
            type="date"
            value={data.date_signed || ''}
            onChange={(e) => onChange('date_signed', e.target.value)}
            readOnly={readOnly}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent read-only:bg-gray-50"
          />
        </div>
      </div>

      {data.scope === 'general' && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <Scale className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            General Power of Attorney grants broad authority over the account.
            Enhanced due diligence may be required.
          </p>
        </div>
      )}
    </div>
  );
};

export default POASection;
